import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { g, p } from "@/lib/api";

/**
 * Blocco 3 — ordine manuale delle comande, trascinandole col dito.
 *
 * Modulo a sé: se il reparto non ha mai trascinato niente, `righe` è esattamente l'elenco che
 * arriva dal server. Non conosce i giri, il gating «da fare ora / in arrivo» né la coda di ritiro.
 */
export const useOrdineManuale = (dept, rows, chi = "") => {
  const [ordine, setOrdine] = useState([]);
  const [dragId, setDragId] = useState("");
  const press = useRef(null);
  const righeRef = useRef([]);

  const carica = useCallback(async () => {
    try {
      const d = await g(`/kds/${dept}/sort`);
      setOrdine(d.order_ids || []);
    } catch {
      setOrdine([]);
    }
  }, [dept]);

  useEffect(() => {
    setOrdine([]);
    carica();
  }, [carica]);

  const righe = useMemo(() => {
    if (!ordine.length) return rows;
    const pos = new Map(ordine.map((id, i) => [id, i]));
    // Le comande arrivate dopo il trascinamento restano in coda, dalla più vecchia.
    const peso = (r) => (pos.has(r.order_id) ? pos.get(r.order_id) : 10000 - (r.minutes || 0));
    return [...rows].sort((a, b) => peso(a) - peso(b));
  }, [rows, ordine]);

  righeRef.current = righe;

  const salva = useCallback(
    (ids) => {
      setOrdine(ids);
      p(`/kds/${dept}/sort`, { order_ids: ids, by: chi }).catch(() => {});
    },
    [dept, chi]
  );

  const azzera = useCallback(() => {
    setOrdine([]);
    p(`/kds/${dept}/sort`, { order_ids: [] }).catch(() => {});
  }, [dept]);

  const handlers = useCallback(
    (id) => ({
      onPointerDown: (e) => {
        if (e.target.closest("button,a,input,select,[data-nodrag]")) return;
        press.current = {
          id, x: e.clientX, y: e.clientY, moved: false,
          seq: righeRef.current.map((r) => r.order_id),
        };
        e.currentTarget.setPointerCapture?.(e.pointerId);
      },
      onPointerMove: (e) => {
        const st = press.current;
        if (!st || st.id !== id) return;
        if (!st.moved) {
          // Soglia di sicurezza: un tocco fermo resta un tocco, non diventa un trascinamento.
          if (Math.hypot(e.clientX - st.x, e.clientY - st.y) < 12) return;
          st.moved = true;
          setDragId(id);
          navigator.vibrate?.(12);
        }
        const sopra = document.elementFromPoint(e.clientX, e.clientY)?.closest("[data-kds-card]");
        const overId = sopra?.getAttribute("data-kds-card");
        if (!overId || overId === st.id) return;
        const seq = [...st.seq];
        const da = seq.indexOf(st.id);
        const a = seq.indexOf(overId);
        if (da < 0 || a < 0) return;
        seq.splice(a, 0, seq.splice(da, 1)[0]);
        st.seq = seq;
        setOrdine(seq);
      },
      onPointerUp: () => {
        const st = press.current;
        press.current = null;
        setDragId("");
        if (st?.moved) salva(st.seq);
      },
      onPointerCancel: () => {
        press.current = null;
        setDragId("");
      },
    }),
    [salva]
  );

  return { righe, dragId, handlers, manuale: ordine.length > 0, azzera };
};
